/**
 * PresentationRepository - Chat data interface
 *
 * Design Principles:
 * - Single interface for chat data operations
 * - Slides live in PowerPoint via Office.js (not managed here)
 * - Provides clean API for components/contexts
 */

import { smartCache } from './SmartCache';

class PresentationRepository {
  constructor() {
    this.cache = smartCache;
  }

  /**
   * Subscribe to presentation changes
   * @param {Function} callback - Called with (id, data) on changes
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    return this.cache.subscribe(callback);
  }

  /**
   * Get cached presentation data
   * @param {string} id - Presentation ID
   * @returns {Object|null} Cached data
   */
  get(id) {
    return this.cache.get(id);
  }

  /**
   * Update presentation with partial data
   * @param {string} id - Presentation ID
   * @param {Object} updates - Partial updates
   */
  async update(id, updates, options = {}) {
    const cacheOptions = {
      skipDirtyFlag: options.skipDirtyTracking === true,
      preserveTimestamp: options.preserveTimestamp === true || options.skipDirtyTracking === true
    };

    const updated = this.cache.update(id, updates, cacheOptions);
    return updated;
  }

  /**
   * Clear cache for a presentation
   */
  clearCache(id) {
    this.cache.clear(id);
  }

  /**
   * Clear all caches
   */
  clearAllCaches() {
    this.cache.clearAll();
  }

  /**
   * Get repository statistics
   */
  getStats() {
    return {
      cache: this.cache.getStats(),
      activeLoads: 0
    };
  }

  // ─── Chat Operations ───────────────────────────────────────

  /**
   * Create welcome message structure for new chats
   * @returns {Object} Welcome message with cards
   */
  createWelcomeMessage() {
    return {
      id: `msg-${Date.now()}`,
      sender: 'ai',
      timestamp: new Date().toISOString(),
      components: [
        {
          type: 'welcome_cards',
          data: {
            title: 'Welcome to Restack',
            subtitle: 'Insert a prompt into your AI consulting workspace',
            cards: [
              {
                id: 'create',
                title: 'Create',
                examples: ['Business strategy', 'Client proposal', 'Board presentation']
              },
              {
                id: 'edit',
                title: 'Edit',
                examples: ['Restructure content', 'Add visuals', 'Improve clarity']
              },
              {
                id: 'research',
                title: 'Research',
                examples: ['Industry trends', 'Market analysis', 'Competitor research']
              },
              {
                id: 'export',
                title: 'Export',
                examples: ['PowerPoint', 'PDF', 'Google Slides']
              }
            ]
          },
          timestamp: new Date().toISOString()
        }
      ],
      isStreaming: false
    };
  }

  /**
   * Create a new chat
   * @param {string} presentationId - Presentation ID
   * @param {Object} chatData - Chat data
   */
  createChat(presentationId, chatData = {}) {
    return this.cache.createChat(presentationId, chatData);
  }

  /**
   * Add message to chat
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   * @param {Object} message - Message data
   */
  addMessage(presentationId, chatId, message) {
    return this.cache.addMessage(presentationId, chatId, message);
  }

  /**
   * Update chat metadata
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   * @param {Object} updates - Partial chat updates
   */
  updateChat(presentationId, chatId, updates = {}) {
    return this.cache.updateChat(presentationId, chatId, updates);
  }

  /**
   * Rename a chat
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   * @param {string} newName - New name
   */
  renameChat(presentationId, chatId, newName) {
    return this.cache.renameChat(presentationId, chatId, newName);
  }

  /**
   * Delete a chat
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   */
  deleteChat(presentationId, chatId) {
    return this.cache.deleteChat(presentationId, chatId);
  }

  /**
   * Clear messages in a chat
   * @param {string} presentationId - Presentation ID
   * @param {string} chatId - Chat ID
   */
  clearChatMessages(presentationId, chatId) {
    return this.cache.clearChatMessages(presentationId, chatId);
  }
}

// Export singleton instance
export const presentationRepository = new PresentationRepository();
export default PresentationRepository;
